import {
  ApolloClient,
  ApolloLink,
  fromPromise,
  InMemoryCache,
  ServerError,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import { API_HOST } from './constants';
import { refreshCurrentSession } from './useAuth';

const apolloCache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        getCmsReservationList: {
          keyArgs: ['accommodationId', 'filter'],
        },
      },
    },
  },
});

const errorLink = onError(
  ({
    graphQLErrors,
    networkError,
    operation,
    forward,
    // eslint-disable-next-line consistent-return
  }) => {
    if (graphQLErrors) {
      let needRefreshToken: boolean = false;
      let needRefreshSubscription: boolean = false;

      graphQLErrors.forEach(({ message, locations, path }) => {
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        );

        needRefreshToken =
          typeof path === 'string' && message.includes('401 Access denied');
        if (
          needRefreshToken &&
          /subscription/i.test(path as unknown as string)
        ) {
          needRefreshSubscription = true;
        }
      });

      if (needRefreshToken) {
        return (
          fromPromise(refreshCurrentSession())
            .filter((value) => Boolean(value.success))
            // @ts-ignore
            .flatMap((result) => {
              if (!result.success) {
                // 토큰 갱신 실패시 에러를 그대로 전파하여 무한 루프 방지
                throw new Error('Token refresh failed');
              }
              if (needRefreshSubscription) {
                console.log(
                  'Refreshing token due to subscription authorize error.'
                );
                window.location.reload();
                return new Observable((observer) => observer.complete());
              }
              return forward(operation);
            })
        );
      }
    }
    if (networkError) {
      const serverError = networkError as ServerError;
      const isUnauthorized = serverError.statusCode === 401;
      const hasInvalidTokenMessage =
        serverError.message?.includes('Not valid token') ||
        (typeof serverError.result === 'object' &&
          serverError.result?.message?.includes('Not valid token')) ||
        JSON.stringify(serverError).includes('Not valid token');

      if (isUnauthorized || hasInvalidTokenMessage) {
        return fromPromise(refreshCurrentSession())
          .filter((value) => Boolean(value.success))
          .flatMap((result) => {
            if (!result.success) {
              // 토큰 갱신 실패시 에러를 그대로 전파하여 무한 루프 방지
              throw new Error('Token refresh failed');
            }
            return forward(operation);
          });
      }
      console.log('[Network error]: ', networkError);
    }
  }
);

const requestLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      let handle: any;
      Promise.resolve(operation)
        .then((oper) => {
          const token = localStorage.getItem('token');
          const service = 'gds_admin';
          if (token) {
            oper.setContext({
              headers: {
                Authorization: `${token}` || null,
                service,
              },
            });
          }
        })
        .then(() => {
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch(observer.error.bind(observer));

      return () => {
        if (handle) handle.unsubscribe();
      };
    })
);

export const apolloClient = new ApolloClient({
  uri: API_HOST,
  link: ApolloLink.from([
    errorLink,
    requestLink,
    createUploadLink({
      uri: `${API_HOST}/graphql`,
    }),
  ]),
  cache: apolloCache,
  defaultOptions: {
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
  },
});
